/**
 * Sentinel Feedback Panel (right column)
 * Feedback form for the response selected in the thread. "Would you have sent
 * the AI response?" -> Yes / No, with reason + rewrite capture on No. Prefilled
 * from the existing review (editable); Save upserts, Delete removes it.
 *
 * The parent remounts this via `key` per selected response, so the form state is
 * initialised straight from the `review` prop.
 */

import { useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type {
  SentinelReason,
  SentinelReviewSummary,
  SentinelVerdict,
} from "@/services/sentinelReview.service";

/** Reasons offered when the verdict is "No"; "other" reveals a free-text note. */
const REASON_OPTIONS: Array<{ value: SentinelReason; label: string; hint?: string }> = [
  { value: "resolution", label: "Resolution", hint: "Resolution - Right action, right outcome" },
  { value: "completeness", label: "Completeness", hint: "Completeness - Addressed every question asked" },
  { value: "facts", label: "Facts", hint: "Facts - No fabricated or wrong facts" },
  { value: "brand_tone_style", label: "Brand Tone & Style", hint: "Tone - Sounds like us" },
  { value: "sentiment", label: "Sentiment", hint: "Sentiment - Matched the customer's sentiment" },
  { value: "other", label: "Other" },
];

export interface SentinelReviewDraft {
  verdict: SentinelVerdict;
  reasons: SentinelReason[];
  otherText?: string;
  rewrite?: string;
}

interface SentinelFeedbackPanelProps {
  /** Existing review for the selected response, if any (prefills + enables delete). */
  review: SentinelReviewSummary | null;
  isSubmitting: boolean;
  onSubmit: (draft: SentinelReviewDraft) => void;
  onDelete: () => void;
}

export function SentinelFeedbackPanel({ review, isSubmitting, onSubmit, onDelete }: SentinelFeedbackPanelProps) {
  const [verdict, setVerdict] = useState<SentinelVerdict | null>(review?.verdict ?? null);
  // Prefill only the codes this panel offers — a review saved under the retired
  // taxonomy would otherwise hold hidden reasons and re-save them.
  const [reasons, setReasons] = useState<Set<SentinelReason>>(
    () => new Set(REASON_OPTIONS.map((o) => o.value).filter((v) => review?.reasons.includes(v))),
  );
  const [rewrite, setRewrite] = useState(review?.rewrite ?? "");
  const [otherText, setOtherText] = useState(review?.otherText ?? "");

  const canSubmit = verdict !== null && !isSubmitting;

  const toggleReason = (reason: SentinelReason) => {
    setReasons((prev) => {
      const next = new Set(prev);
      if (next.has(reason)) next.delete(reason);
      else next.add(reason);
      return next;
    });
  };

  const handleSubmit = () => {
    if (verdict === null) return;
    const draft: SentinelReviewDraft = {
      verdict,
      reasons: verdict === "no" ? Array.from(reasons) : [],
    };
    if (verdict === "no") {
      const trimmedRewrite = rewrite.trim();
      if (trimmedRewrite) draft.rewrite = trimmedRewrite;
      const trimmedOther = otherText.trim();
      if (reasons.has("other") && trimmedOther) draft.otherText = trimmedOther;
    }
    onSubmit(draft);
  };

  return (
    <div className="flex h-full w-full flex-col border-l border-border bg-background">
      <div className="flex h-16 shrink-0 items-center border-b border-border px-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Feedback</h2>
          <p className="text-xs text-muted-foreground">
            {review ? "Editing your review" : "Review this response"}
          </p>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <div className="flex flex-col gap-4">
          <VerdictCard verdict={verdict} onChange={setVerdict} />

          {verdict === "no" && (
            <>
              <ReasonCard
                reasons={reasons}
                onToggle={toggleReason}
                otherText={otherText}
                onOtherTextChange={setOtherText}
              />
              <RewriteCard rewrite={rewrite} onChange={setRewrite} />
            </>
          )}
        </div>
      </div>

      <div className="shrink-0 space-y-2 border-t border-border px-4 py-3">
        <Button className="w-full" onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting ? "Saving…" : review ? "Update feedback" : "Save feedback"}
        </Button>
        {review && (
          <Button
            variant="ghost"
            className="w-full text-destructive hover:text-destructive"
            onClick={onDelete}
            disabled={isSubmitting}
          >
            Delete feedback
          </Button>
        )}
      </div>
    </div>
  );
}

function VerdictCard({
  verdict,
  onChange,
}: {
  verdict: SentinelVerdict | null;
  onChange: (verdict: SentinelVerdict) => void;
}) {
  return (
    <div className="rounded-xl border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">Would you have sent the AI response?</h3>
      <p className="mt-1 text-xs text-muted-foreground">Judge Kim's response shown on the left.</p>
      <div className="mt-4 flex flex-col gap-2">
        <VerdictOption value="yes" label="Yes" selected={verdict === "yes"} onSelect={onChange} tone="positive" />
        <VerdictOption value="no" label="No" selected={verdict === "no"} onSelect={onChange} tone="negative" />
      </div>
    </div>
  );
}

function VerdictOption({
  value,
  label,
  selected,
  onSelect,
  tone,
}: {
  value: SentinelVerdict;
  label: string;
  selected: boolean;
  onSelect: (value: SentinelVerdict) => void;
  tone: "positive" | "negative";
}) {
  const selectedClass = tone === "positive" ? "border-green-border bg-green-bg" : "border-red-border bg-red-bg";
  const checkClass = tone === "positive" ? "text-green-foreground" : "text-red-foreground";

  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={cn(
        "flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-colors",
        selected ? selectedClass : "border-border hover:bg-muted/40",
      )}
    >
      <span className="flex items-center gap-2.5">
        <span
          className={cn(
            "flex size-5 items-center justify-center rounded-full text-primary-foreground",
            tone === "positive" ? "bg-green-foreground" : "bg-destructive",
          )}
        >
          {tone === "positive" ? <Check className="size-3" /> : <X className="size-3" />}
        </span>
        <span className="text-sm font-medium text-foreground">{label}</span>
      </span>
      {selected && <Check className={cn("size-4", checkClass)} />}
    </button>
  );
}

function ReasonCard({
  reasons,
  onToggle,
  otherText,
  onOtherTextChange,
}: {
  reasons: Set<SentinelReason>;
  onToggle: (reason: SentinelReason) => void;
  otherText: string;
  onOtherTextChange: (value: string) => void;
}) {
  return (
    <div className="rounded-xl border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">What was wrong with it?</h3>
      <p className="mt-1 text-xs text-muted-foreground">Select as many as they apply</p>
      <div className="mt-3 flex flex-col gap-3">
        {REASON_OPTIONS.map((option) => (
          <div key={option.value} className="flex flex-col gap-2">
            <label className="flex cursor-pointer items-start gap-2.5">
              <Checkbox
                checked={reasons.has(option.value)}
                onCheckedChange={() => onToggle(option.value)}
                className="mt-0.5 border-border shadow-none"
              />
              <span className="flex flex-col">
                <span className="text-sm text-foreground">{option.label}</span>
                {option.hint && <span className="text-[11px] text-muted-foreground">{option.hint}</span>}
              </span>
            </label>
            {option.value === "other" && reasons.has("other") && (
              <Textarea
                value={otherText}
                onChange={(event) => onOtherTextChange(event.target.value)}
                placeholder="Add a note"
                className="min-h-16"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RewriteCard({ rewrite, onChange }: { rewrite: string; onChange: (value: string) => void }) {
  const helper = useMemo(() => "Share what you would have written or sent instead", []);
  return (
    <div className="rounded-xl border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">Elaborate on your feedback</h3>
      <p className="mt-1 text-xs text-muted-foreground">{helper}</p>
      <Textarea
        value={rewrite}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Describe what you'd send"
        className="mt-3 min-h-24"
      />
    </div>
  );
}
